import { useState } from 'react'
import { Alert, Button, Dialog, Field, Input, Text, Textarea, VStack } from '@chakra-ui/react'
import { FiEdit2, FiPlus, FiTrash2 } from 'react-icons/fi'
import { PageContainer } from '../components/shared/PageContainer'
import { ResponsiveModal } from '../components/shared/ResponsiveModal'
import { ResponsiveTable, type ResponsiveColumn } from '../components/shared/ResponsiveTable'
import { useDeleteTemplate, useEmailTemplates, useSaveTemplate, useUpdateTemplate } from '../hooks/useCommunications'
import { extractApiMessage } from '../lib/apiError'
import { formatShortDate } from '../lib/formatDate'
import type { EmailTemplateDto } from '../types/communications'

const columns: ResponsiveColumn<EmailTemplateDto>[] = [
  { header: 'Name', mobileRole: 'title', cell: (t) => <Text fontWeight="medium">{t.name}</Text> },
  { header: 'Subject', mobileRole: 'meta', cell: (t) => <Text fontSize="sm" color="fg.muted">{t.subject}</Text> },
  { header: 'Updated', mobileRole: 'meta', cell: (t) => <Text fontSize="sm" color="fg.muted">{formatShortDate(t.updatedAt)}</Text> },
]

/** Spec §8 `/communications/templates` -- list + edit drawer. Also where the compose page's
 * "Start from template ▾" / "Save as template" flows point admins/organizers who want to
 * manage the full set, not just pick one. Create and update are two different mutations
 * (useSaveTemplate/useUpdateTemplate) with different call shapes -- a mutation only takes one
 * variable, and update needs the target id alongside the new fields -- so the drawer tracks
 * which target it's editing ('new' | the EmailTemplateDto being edited | null) and picks the
 * matching mutation at submit time rather than forcing both into one hook shape. */
export function EmailTemplatesPage() {
  const { data: templates, isLoading, error } = useEmailTemplates()
  const saveTemplate = useSaveTemplate()
  const updateTemplate = useUpdateTemplate()
  const deleteTemplate = useDeleteTemplate()

  const [editTarget, setEditTarget] = useState<EmailTemplateDto | 'new' | null>(null)
  const [name, setName] = useState('')
  const [subject, setSubject] = useState('')
  const [body, setBody] = useState('')
  const [deleteTarget, setDeleteTarget] = useState<EmailTemplateDto | null>(null)

  const isNew = editTarget === 'new'
  const activeMutation = isNew ? saveTemplate : updateTemplate

  const openNew = () => {
    setEditTarget('new')
    setName('')
    setSubject('')
    setBody('')
  }

  const openEdit = (t: EmailTemplateDto) => {
    setEditTarget(t)
    setName(t.name)
    setSubject(t.subject)
    setBody(t.bodyMarkdown)
  }

  const closeEdit = () => setEditTarget(null)

  const handleSubmit = () => {
    const payload = { name: name.trim(), subject: subject.trim(), bodyMarkdown: body }
    if (isNew) {
      saveTemplate.mutate(payload, { onSuccess: closeEdit })
    } else if (editTarget) {
      updateTemplate.mutate({ id: editTarget.id, data: payload }, { onSuccess: closeEdit })
    }
  }

  const closeDelete = () => setDeleteTarget(null)

  const confirmDelete = () => {
    if (!deleteTarget) return
    // mutate, not mutateAsync: only close the dialog on actual success so a failed delete
    // leaves the confirmation open with its error visible (ReportRunsPage precedent).
    deleteTemplate.mutate(deleteTarget.id, { onSuccess: closeDelete })
  }

  return (
    <PageContainer
      title="Email templates"
      subtitle="Reusable subject + body starting points for compose."
      actions={<Button colorPalette="brand" onClick={openNew}><FiPlus />New template</Button>}
    >
      {!!error && <Text color="danger.fg" mb={4}>Could not load templates.</Text>}
      <ResponsiveTable
        columns={columns}
        rows={templates ?? []}
        keyFn={(t) => t.id}
        isLoading={isLoading}
        emptyMessage="No templates yet."
        rowActions={[
          { label: 'Edit', icon: FiEdit2, onClick: (t) => openEdit(t) },
          { label: 'Delete', icon: FiTrash2, destructive: true, onClick: (t) => setDeleteTarget(t) },
        ]}
      />

      <ResponsiveModal isOpen={editTarget !== null} onClose={closeEdit}>
        <Dialog.Backdrop />
        <Dialog.Positioner>
          <Dialog.Content>
            <Dialog.Header fontSize="lg" fontWeight="bold">
              {isNew ? 'New template' : 'Edit template'}
            </Dialog.Header>
            <Dialog.Body>
              <VStack align="stretch" gap={4}>
                <Field.Root required>
                  <Field.Label fontSize="sm" color="fg.muted">Name</Field.Label>
                  <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Lobby Day reminder" />
                </Field.Root>
                <Field.Root required>
                  <Field.Label fontSize="sm" color="fg.muted">Subject</Field.Label>
                  <Input value={subject} onChange={(e) => setSubject(e.target.value)} />
                </Field.Root>
                <Field.Root required>
                  <Field.Label fontSize="sm" color="fg.muted">Body (Markdown)</Field.Label>
                  <Textarea value={body} onChange={(e) => setBody(e.target.value)} rows={10} />
                </Field.Root>
                {!!activeMutation.error && (
                  <Alert.Root status="error" borderRadius="md">
                    <Alert.Indicator />
                    <Alert.Content>{extractApiMessage(activeMutation.error, 'Could not save this template.')}</Alert.Content>
                  </Alert.Root>
                )}
              </VStack>
            </Dialog.Body>
            <Dialog.Footer>
              <Button onClick={closeEdit} disabled={activeMutation.isPending}>Cancel</Button>
              <Button
                colorPalette="brand"
                ml={3}
                onClick={handleSubmit}
                disabled={!name.trim() || !subject.trim() || !body.trim()}
                loading={activeMutation.isPending}
              >
                Save
              </Button>
            </Dialog.Footer>
          </Dialog.Content>
        </Dialog.Positioner>
      </ResponsiveModal>

      <ResponsiveModal isOpen={!!deleteTarget} onClose={closeDelete}>
        <Dialog.Backdrop />
        <Dialog.Positioner>
          <Dialog.Content role="alertdialog">
            <Dialog.Header fontSize="lg" fontWeight="bold">Delete template</Dialog.Header>
            <Dialog.Body>
              <Text>
                Are you sure you want to delete <strong>{deleteTarget?.name}</strong>? This cannot be undone.
              </Text>
              {!!deleteTemplate.error && (
                <Alert.Root status="error" borderRadius="md" mt={3}>
                  <Alert.Indicator />
                  <Alert.Content>{extractApiMessage(deleteTemplate.error, 'Could not delete this template.')}</Alert.Content>
                </Alert.Root>
              )}
            </Dialog.Body>
            <Dialog.Footer>
              <Button onClick={closeDelete} disabled={deleteTemplate.isPending}>Cancel</Button>
              <Button colorPalette="red" ml={3} onClick={confirmDelete} loading={deleteTemplate.isPending}>
                Delete
              </Button>
            </Dialog.Footer>
          </Dialog.Content>
        </Dialog.Positioner>
      </ResponsiveModal>
    </PageContainer>
  )
}
